// The radio for Bluetooth Mesh lamps: PB-GATT (pairing, service 0x1827) and
// the Mesh Proxy (control, service 0x1828), with Proxy PDU segmentation.
//
// Ported from the cob-mesh-reactjs sampler (src/ble/meshGatt.ts), moved from
// react-native-ble-plx onto @capacitor-community/bluetooth-le — the plugin the
// app already ships, so this needs NO native change and travels by OTA.
//
// A THIRD way of talking Bluetooth, next to two others — keep them apart:
//   core/ble/bleClient.js     GATT to Smartify ESP hardware (auth, Wi-Fi setup)
//   core/ble/lamp/            ADVERTISING to LampSmart/FanLamp lamps (no reply)
//   core/ble/mesh/ (this)     GATT to ONE mesh lamp, which relays to the rest
//
// ⚠️ MTU. The plugin negotiates it on its own inside connect() (Android
// Device.kt asks for 512; iOS negotiates itself), so we only READ it. Proxy
// PDUs are split to fit, so a small MTU is slower, never wrong.
//
// ⚠️ WRITES ARE SERIALISED. Overlapping GATT writes fail silently on many
// phones (the same rule as bleClient.js); a lost segment of an AppKey Add is a
// configure that times out for no visible reason.

import { BleClient, ScanMode } from '@capacitor-community/bluetooth-le';
import { ensureBleReady, stopScan } from '../bleClient';
import {
  concat, fromDataView, toDataView,
} from './bytes';

/* eslint-disable no-bitwise */

export const UUID = {
  PROV_SERVICE: '00001827-0000-1000-8000-00805f9b34fb',
  PROV_IN: '00002adb-0000-1000-8000-00805f9b34fb',
  PROV_OUT: '00002adc-0000-1000-8000-00805f9b34fb',
  PROXY_SERVICE: '00001828-0000-1000-8000-00805f9b34fb',
  PROXY_IN: '00002add-0000-1000-8000-00805f9b34fb',
  PROXY_OUT: '00002ade-0000-1000-8000-00805f9b34fb',
};

export const MSG = {
  NETWORK: 0, BEACON: 1, PROXY_CONFIG: 2, PROVISIONING: 3,
};

const CONNECT_TIMEOUT_MS = 15000;
const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/**
 * Scan for mesh advertisements. `onDevice({ deviceId, name, rssi, kind, uuid?, networkId? })`
 * kind 'unprovisioned' (0x1827: ready to pair) or 'proxy' (0x1828: a paired lamp).
 * Returns a stop function; the scan also stops itself after `timeoutMs`.
 */
export async function scanMesh(onDevice, { timeoutMs = 12000, services = [UUID.PROV_SERVICE, UUID.PROXY_SERVICE] } = {}) {
  await ensureBleReady();
  let stopped = false;
  const stop = async () => {
    if (stopped) return;
    stopped = true;
    await stopScan();
  };
  await BleClient.requestLEScan({
    services,
    // Duplicates on, low latency: the same reasons as bleClient.scanForDevices —
    // a first sighting without a name must not be the only one we get.
    allowDuplicates: true,
    scanMode: ScanMode.SCAN_MODE_LOW_LATENCY,
  }, (result) => {
    if (stopped) return;
    const parsed = parseMeshAdvert(result);
    if (parsed) onDevice(parsed);
  });
  setTimeout(() => { stop(); }, timeoutMs);
  return stop;
}

/** One scan result -> { deviceId, name, rssi, kind, uuid?, oobInfo?, networkId? } or null. */
export function parseMeshAdvert(result) {
  const deviceId = result?.device?.deviceId;
  if (!deviceId) return null;
  const sd = result.serviceData || {};
  const key = (short) => Object.keys(sd).find((k) => k.toLowerCase().includes(short));
  const uuids = (result.uuids || []).map((u) => u.toLowerCase());
  const base = {
    deviceId,
    name: result.localName || result.device?.name || '',
    rssi: result.rssi ?? 0,
    lastSeen: Date.now(),
  };
  const provKey = key('00001827');
  if (provKey || uuids.some((u) => u.startsWith('00001827'))) {
    const data = provKey ? fromDataView(sd[provKey]) : new Uint8Array(0);
    return {
      ...base,
      kind: 'unprovisioned',
      uuid: data.length >= 16 ? Array.from(data.slice(0, 16), (x) => x.toString(16).padStart(2, '0')).join('') : undefined,
      oobInfo: data.length >= 18 ? (data[16] << 8) | data[17] : undefined,
    };
  }
  const proxyKey = key('00001828');
  if (proxyKey || uuids.some((u) => u.startsWith('00001828'))) {
    const data = proxyKey ? fromDataView(sd[proxyKey]) : new Uint8Array(0);
    // Type 0x00 = Network ID (k3 of the NetKey): tells us WHICH network this
    // proxy belongs to without connecting. Type 0x01 = Node Identity (hashed),
    // which a lamp advertises for a minute after pairing.
    const networkId = data.length >= 9 && data[0] === 0x00
      ? Array.from(data.slice(1, 9), (x) => x.toString(16).padStart(2, '0')).join('')
      : undefined;
    // Node Identity: Hash (8) || Random (8). Matched against our lamps'
    // addresses in meshSession.matchesNodeIdentity.
    const nodeIdentity = data.length >= 17 && data[0] === 0x01
      ? { hash: data.slice(1, 9), random: data.slice(9, 17) }
      : undefined;
    return {
      ...base, kind: 'proxy', networkId, nodeIdentity,
    };
  }
  return null;
}

export class MeshGatt {
  constructor(log = () => {}) {
    this.log = log;
    this.deviceId = null;
    this.kind = null;
    this.mtu = 23;
    this.rxBuf = null;
    this.rxType = 0;
    this.provQueue = [];
    this.provWaiters = [];
    this.chain = Promise.resolve();
    this.onNetworkPdu = null;
    this.onProxyConfigPdu = null;
    this.onDisconnect = null;
  }

  get connected() { return !!this.deviceId; }

  async connect(deviceId, kind) {
    await ensureBleReady();
    const svc = kind === 'prov' ? UUID.PROV_SERVICE : UUID.PROXY_SERVICE;
    const out = kind === 'prov' ? UUID.PROV_OUT : UUID.PROXY_OUT;
    this.log(`Connecting (${kind === 'prov' ? 'pairing' : 'proxy'})…`);
    await BleClient.connect(deviceId, () => {
      if (this.deviceId !== deviceId) return;
      this.log('Disconnected');
      this.cleanup();
      this.onDisconnect?.();
    }, { timeout: CONNECT_TIMEOUT_MS });
    try {
      const services = await BleClient.getServices(deviceId);
      if (!services.some((s) => String(s.uuid).toLowerCase() === svc)) {
        throw new Error(kind === 'prov'
          ? 'This lamp is not in pairing mode (no 0x1827 service). Reset it and try again.'
          : 'This lamp is not acting as a mesh proxy (no 0x1828 service).');
      }
      try { this.mtu = (await BleClient.getMtu(deviceId)) || 23; } catch (e) { this.mtu = 23; }
      this.deviceId = deviceId;
      this.kind = kind;
      this.rxBuf = null;
      // Subscribe BEFORE the first write, or the first reply is lost.
      await BleClient.startNotifications(deviceId, svc, out, (dv) => this.onFragment(fromDataView(dv)));
      await sleep(300); // let the CCCD write settle
      this.log(`Connected, MTU ${this.mtu}`);
    } catch (e) {
      this.deviceId = null;
      await BleClient.disconnect(deviceId).catch(() => {});
      throw e;
    }
  }

  async disconnect() {
    const id = this.deviceId;
    this.cleanup();
    if (id) await BleClient.disconnect(id).catch(() => {});
  }

  cleanup() {
    this.deviceId = null;
    this.kind = null;
    this.rxBuf = null;
    this.provQueue = [];
  }

  // ----- receive, with SAR reassembly -----
  onFragment(f) {
    if (!f.length) return;
    const sar = f[0] >> 6;
    const type = f[0] & 0x3f;
    const body = f.slice(1);
    if (sar === 0) { this.dispatch(type, body); return; }
    if (sar === 1) { this.rxBuf = body; this.rxType = type; return; }
    if (!this.rxBuf) return;
    this.rxBuf = concat(this.rxBuf, body);
    if (sar === 3) {
      const b = this.rxBuf;
      this.rxBuf = null;
      this.dispatch(this.rxType, b);
    }
  }

  dispatch(type, body) {
    if (type === MSG.PROVISIONING) {
      const w = this.provWaiters.shift();
      if (w) w(body); else this.provQueue.push(body);
    } else if (type === MSG.NETWORK) {
      this.onNetworkPdu?.(body);
    } else if (type === MSG.PROXY_CONFIG) {
      this.onProxyConfigPdu?.(body);
    }
    // Mesh beacons (type 1) are ignored.
  }

  /** The next provisioning PDU from the lamp. */
  nextProv(timeoutMs) {
    const q = this.provQueue.shift();
    if (q) return Promise.resolve(q);
    return new Promise((resolve, reject) => {
      const done = (p) => { clearTimeout(t); resolve(p); };
      const t = setTimeout(() => {
        this.provWaiters = this.provWaiters.filter((w) => w !== done);
        reject(new Error(`The lamp stopped answering (${timeoutMs / 1000} s)`));
      }, timeoutMs);
      this.provWaiters.push(done);
    });
  }

  // ----- send, with SAR segmentation -----
  send(type, payload) {
    const run = this.chain.then(() => this.sendNow(type, payload));
    this.chain = run.then(() => undefined, () => undefined);
    return run;
  }

  async sendNow(type, payload) {
    const id = this.deviceId;
    if (!id) throw new Error('Not connected to a lamp');
    const svc = type === MSG.PROVISIONING ? UUID.PROV_SERVICE : UUID.PROXY_SERVICE;
    const ch = type === MSG.PROVISIONING ? UUID.PROV_IN : UUID.PROXY_IN;
    const max = Math.max(20, this.mtu - 3) - 1;
    const write = (b) => BleClient.writeWithoutResponse(id, svc, ch, toDataView(b));
    if (payload.length <= max) {
      await write(concat(Uint8Array.of(type), payload));
      return;
    }
    for (let i = 0; i < payload.length; i += max) {
      let sar = 2;
      if (i === 0) sar = 1;
      else if (i + max >= payload.length) sar = 3;
      // eslint-disable-next-line no-await-in-loop
      await write(concat(Uint8Array.of((sar << 6) | type), payload.slice(i, i + max)));
      // eslint-disable-next-line no-await-in-loop
      await sleep(20);
    }
  }
}
